/**
 * Visual effects for ability casts — projectiles, AoE rings, buff auras, dash trails.
 */

import * as THREE from "three";
import { AbilityType, type AbilityCastResult } from "@craftgaming/shared";

type ActiveEffect = {
  mesh: THREE.Object3D;
  lifetime: number;
  maxLifetime: number;
  type: AbilityType;
  velocity?: THREE.Vector3;
  startScale?: number;
  update: (dt: number) => boolean; // returns false when done
};

export type AbilityEffects = {
  spawn: (cast: AbilityCastResult) => void;
  update: (dt: number) => void;
  dispose: () => void;
};

export function createAbilityEffects(scene: THREE.Scene): AbilityEffects {
  const effects: ActiveEffect[] = [];

  function spawn(cast: AbilityCastResult) {
    if (!cast.success || !cast.ability || !cast.position || !cast.direction) return;

    const { ability, position, direction } = cast;
    const color = new THREE.Color(ability.color);

    switch (ability.type) {
      case AbilityType.PROJECTILE:
        spawnProjectile(position, direction, color, ability.range, ability.damage);
        break;
      case AbilityType.AREA:
        spawnAreaEffect(position, color, ability.range);
        break;
      case AbilityType.BUFF:
        spawnBuffAura(position, color, ability.duration);
        break;
      case AbilityType.DASH:
        spawnDashTrail(position, direction, color);
        break;
    }
  }

  function spawnProjectile(
    pos: { x: number; y: number; z: number },
    dir: { x: number; y: number; z: number },
    color: THREE.Color,
    range: number,
    _damage: number
  ) {
    // Glowing sphere
    const geo = new THREE.SphereGeometry(0.25, 8, 8);
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9 });
    const sphere = new THREE.Mesh(geo, mat);
    sphere.position.set(pos.x, pos.y + 1.5, pos.z);

    // Glow sprite
    const glowGeo = new THREE.SphereGeometry(0.5, 8, 8);
    const glowMat = new THREE.MeshBasicMaterial({
      color, transparent: true, opacity: 0.3,
    });
    const glow = new THREE.Mesh(glowGeo, glowMat);
    sphere.add(glow);

    scene.add(sphere);

    const speed = 30;
    const vel = new THREE.Vector3(dir.x, dir.y, dir.z).normalize().multiplyScalar(speed);
    const maxLife = range / speed;

    effects.push({
      mesh: sphere,
      lifetime: 0,
      maxLifetime: maxLife,
      type: AbilityType.PROJECTILE,
      velocity: vel,
      update(dt) {
        this.lifetime += dt;
        sphere.position.add(vel.clone().multiplyScalar(dt));

        // Pulse
        const pulse = 1 + Math.sin(this.lifetime * 15) * 0.15;
        sphere.scale.setScalar(pulse);

        // Fade at end
        const remaining = 1 - this.lifetime / this.maxLifetime;
        mat.opacity = remaining * 0.9;
        glowMat.opacity = remaining * 0.3;

        return this.lifetime < this.maxLifetime;
      },
    });
  }

  function spawnAreaEffect(
    pos: { x: number; y: number; z: number },
    color: THREE.Color,
    range: number
  ) {
    // Expanding ring on the ground
    const geo = new THREE.RingGeometry(0.5, 0.8, 32);
    const mat = new THREE.MeshBasicMaterial({
      color, transparent: true, opacity: 0.7, side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(geo, mat);
    ring.position.set(pos.x, pos.y + 0.1, pos.z);
    ring.rotation.x = -Math.PI / 2;
    scene.add(ring);

    const maxLife = 0.8;
    const targetScale = range / 0.8;

    effects.push({
      mesh: ring,
      lifetime: 0,
      maxLifetime: maxLife,
      type: AbilityType.AREA,
      update(dt) {
        this.lifetime += dt;
        const t = this.lifetime / this.maxLifetime;

        // Expand
        const scale = t * targetScale;
        ring.scale.setScalar(scale);

        // Fade out
        mat.opacity = (1 - t) * 0.7;

        return this.lifetime < this.maxLifetime;
      },
    });

    // Vertical pillar particles
    const pillarGeo = new THREE.CylinderGeometry(0.3, range * 0.5, 4, 8, 1, true);
    const pillarMat = new THREE.MeshBasicMaterial({
      color, transparent: true, opacity: 0.2, side: THREE.DoubleSide,
    });
    const pillar = new THREE.Mesh(pillarGeo, pillarMat);
    pillar.position.set(pos.x, pos.y + 2, pos.z);
    scene.add(pillar);

    effects.push({
      mesh: pillar,
      lifetime: 0,
      maxLifetime: 0.6,
      type: AbilityType.AREA,
      update(dt) {
        this.lifetime += dt;
        const t = this.lifetime / this.maxLifetime;
        pillarMat.opacity = (1 - t) * 0.2;
        pillar.scale.y = 1 + t * 0.5;
        return this.lifetime < this.maxLifetime;
      },
    });
  }

  function spawnBuffAura(
    pos: { x: number; y: number; z: number },
    color: THREE.Color,
    duration: number
  ) {
    // Swirling particles around player
    const group = new THREE.Group();
    group.position.set(pos.x, pos.y + 1, pos.z);

    const particleCount = 12;
    const particles: THREE.Mesh[] = [];

    for (let i = 0; i < particleCount; i++) {
      const geo = new THREE.SphereGeometry(0.08, 4, 4);
      const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.6 });
      const p = new THREE.Mesh(geo, mat);
      const angle = (i / particleCount) * Math.PI * 2;
      p.position.set(Math.cos(angle) * 0.8, (i / particleCount) * 1.5 - 0.5, Math.sin(angle) * 0.8);
      group.add(p);
      particles.push(p);
    }

    scene.add(group);

    const maxLife = Math.min(duration, 2); // visual max 2s

    effects.push({
      mesh: group,
      lifetime: 0,
      maxLifetime: maxLife,
      type: AbilityType.BUFF,
      update(dt) {
        this.lifetime += dt;
        group.rotation.y += dt * 3;

        // Spiral upward
        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          const baseAngle = (i / particleCount) * Math.PI * 2 + this.lifetime * 2;
          const radius = 0.8 + Math.sin(this.lifetime * 4 + i) * 0.2;
          p.position.x = Math.cos(baseAngle) * radius;
          p.position.z = Math.sin(baseAngle) * radius;
          p.position.y += dt * 0.5;

          const mat = p.material as THREE.MeshBasicMaterial;
          mat.opacity = Math.max(0, 0.6 * (1 - this.lifetime / this.maxLifetime));
        }

        return this.lifetime < this.maxLifetime;
      },
    });
  }

  function spawnDashTrail(
    pos: { x: number; y: number; z: number },
    dir: { x: number; y: number; z: number },
    color: THREE.Color
  ) {
    // After-image trail
    for (let i = 0; i < 5; i++) {
      const geo = new THREE.BoxGeometry(0.4, 1.6, 0.4);
      const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.4 });
      const ghost = new THREE.Mesh(geo, mat);

      const offset = i * 0.6;
      ghost.position.set(
        pos.x - dir.x * offset,
        pos.y + 0.8,
        pos.z - dir.z * offset
      );
      scene.add(ghost);

      const delay = i * 0.05;

      effects.push({
        mesh: ghost,
        lifetime: -delay,
        maxLifetime: 0.5,
        type: AbilityType.DASH,
        update(dt) {
          this.lifetime += dt;
          if (this.lifetime < 0) return true;
          const t = this.lifetime / this.maxLifetime;
          mat.opacity = (1 - t) * 0.4;
          ghost.scale.y = 1 - t * 0.5;
          ghost.position.y += dt * 0.5;
          return this.lifetime < this.maxLifetime;
        },
      });
    }
  }

  function update(dt: number) {
    for (let i = effects.length - 1; i >= 0; i--) {
      const alive = effects[i].update(dt);
      if (!alive) {
        scene.remove(effects[i].mesh);
        const mat = effects[i].mesh as THREE.Mesh;
        if (mat.geometry) mat.geometry.dispose();
        effects.splice(i, 1);
      }
    }
  }

  function dispose() {
    for (const effect of effects) {
      scene.remove(effect.mesh);
    }
    effects.length = 0;
  }

  return { spawn, update, dispose };
}
